/**
 * Utility functions to check booking availability at Event level only
 * No slot or sub-event checks - only event-level booking times
 */
/**
 * Checks if the current time is within the global event's booking window
 * @param event - The event object
 * @returns true if booking is allowed based on global booking times
 */
export const isGlobalBookingTimeValid = (event) => {
    if (!event)
        return false;
    const now = new Date();
    // Check if booking_start_time exists
    if (event.booking_start_time) {
        const bookingStartTime = new Date(event.booking_start_time);
        if (now < bookingStartTime) {
            return false; // Booking hasn't started yet
        }
    }
    // Check if booking_end_time exists
    if (event.booking_end_time) {
        const bookingEndTime = new Date(event.booking_end_time);
        if (now > bookingEndTime) {
            return false; // Booking has ended
        }
    }
    return true;
};
/**
 * Comprehensive check for event booking availability
 * Only checks event-level properties, not slots or sub-events
 * @param event - The event object
 * @returns object with isAllowed flag and reason for disabling
 */
export const checkEventBookingAvailability = (event) => {
    if (!event) {
        return { isAllowed: false, reason: "Event not found" };
    }
    // Check if event is active
    if (!event.is_active) {
        return { isAllowed: false, reason: "Event is not active" };
    }
    // Check if booking is allowed
    if (!event.booking_allowed) {
        return { isAllowed: false, reason: "Booking not allowed" };
    }
    // Check global booking time window
    if (!isGlobalBookingTimeValid(event)) {
        return { isAllowed: false, reason: "Outside booking time window" };
    }
    // Check if event has ended
    const now = new Date();
    const eventEndDate = new Date(event.event_end_date);
    if (now > eventEndDate) {
        return { isAllowed: false, reason: "Event has ended" };
    }
    return { isAllowed: true };
};
/**
 * Checks if a time slot should be shown based on buffer time
 * Buffer time is the minimum advance booking time in minutes
 * @param slotStartTime - The start time of the slot
 * @param bufferMinutes - Buffer time in minutes from event.buffer_time_minutes
 * @returns true if the slot should be shown (slot starts after buffer time from now)
 */
export const isTimeSlotWithinBuffer = (slotStartTime, bufferMinutes = 0) => {
    // If buffer time is 0, null, undefined, or any falsy value, show all slots (normal flow)
    if (!bufferMinutes || bufferMinutes === 0)
        return true;
    const now = new Date();
    const minimumBookableTime = new Date(now.getTime() + bufferMinutes * 60 * 1000);
    // Show slot only if it starts after the minimum bookable time
    return slotStartTime >= minimumBookableTime;
};
